import { useCallback, useState } from 'react'
import { ResponseVariant } from 'workflow/Insights/Insights'

import { errorToast } from 'common/components/toastNotification'
import { useAppDispatch } from 'common/hooks/redux'
import useApproveSingleVariantMutation from 'features/unifiedFlow/api/mutations/useApproveSingleVariantMutation'
import { showRightPanel } from 'features/unifiedFlow/store/unifiedFlowSlice'

import { useVariantLanguageContext } from '../../review/context/VariantLanguageContext'
import { getVariantId } from '../utils/variantUtils'

import { ResponseVariantExtended } from './useManageOptimizeVariants'

type Content =
  | {
      _id: string
      account_id: string
    }
  | null
  | undefined

type SelectedElement =
  | {
      element_id: number | string
    }
  | null
  | undefined

type UseCommonHandlersProps = {
  variantsToBeApprovedFiltered: ResponseVariantExtended[]
  onSelectVariant: (variant: ResponseVariant) => void

  // For approve/drop logic
  content: Content
  selectedElement: SelectedElement

  // Flow-specific drop function
  dropFn: (variantId: string) => Promise<void>

  // Callbacks after mutations (optional)
  onApproveSuccess?: (variantId: string) => void
  onDropSuccess?: (variantId: string) => void
  onBulkApproveSuccess?: (variantIds: string[]) => void
  onBulkDropSuccess?: (variantIds: string[]) => void
}

/**
 * Shared hook for managing variants across optimize and personalize flows.
 * Handles common state management and handlers for variant approval/dropping.
 */
export const useCommonHandlers = ({
  variantsToBeApprovedFiltered,
  onSelectVariant,
  content,
  selectedElement,
  dropFn,
  onApproveSuccess,
  onDropSuccess,
  onBulkApproveSuccess,
  onBulkDropSuccess,
}: UseCommonHandlersProps) => {
  const dispatch = useAppDispatch()
  const { selectVariant } = useVariantLanguageContext()
  const [selectedVariantIds, setSelectedVariantIds] = useState<string[]>([])
  const [approvingVariantIds, setApprovingVariantIds] = useState<string[]>([])
  const [droppingVariantIds, setDroppingVariantIds] = useState<string[]>([])
  const approveSingleVariant = useApproveSingleVariantMutation()

  const handleCheckboxClick = useCallback(
    (variant: ResponseVariantExtended) => {
      const variantId = getVariantId(variant)
      if (!variantId) {
        return
      }
      setSelectedVariantIds((prev) => {
        if (prev.includes(variantId)) {
          return prev.filter((id) => id !== variantId)
        } else {
          return [...prev, variantId]
        }
      })
    },
    []
  )

  const handleRowClick = useCallback(
    (variant: ResponseVariantExtended) => {
      selectVariant({
        combinationId: variant.combinationId,
        variantId: variant._id,
      })
      onSelectVariant(variant)
      dispatch(showRightPanel())
    },
    [selectVariant, onSelectVariant, dispatch]
  )

  const handleSelectAll = useCallback(() => {
    if (selectedVariantIds.length === variantsToBeApprovedFiltered.length) {
      setSelectedVariantIds([])
    } else {
      setSelectedVariantIds(
        variantsToBeApprovedFiltered
          .map((variant) => getVariantId(variant))
          .filter((id): id is string => Boolean(id))
      )
    }
  }, [selectedVariantIds, variantsToBeApprovedFiltered])

  const handleApproveVariant = useCallback(
    (variant: ResponseVariantExtended) => {
      if (!content || !selectedElement) {
        return
      }

      const variantId = getVariantId(variant)
      if (!variantId) {
        return
      }
      setApprovingVariantIds((prev) => [...prev, variantId])

      approveSingleVariant.mutate(
        {
          accountId: content.account_id,
          content_id: content._id,
          element_id: Number(selectedElement.element_id),
          variant_ids: [variantId],
        },
        {
          onSuccess: () => {
            setSelectedVariantIds((prev) =>
              prev.filter((id) => id !== variantId)
            )
            onApproveSuccess?.(variantId)
          },
          onError: () => {
            errorToast('Failed to approve variant. Please try again.')
          },
          onSettled: () => {
            setApprovingVariantIds((prev) =>
              prev.filter((id) => id !== variantId)
            )
          },
        }
      )
    },
    [approveSingleVariant, content, selectedElement, onApproveSuccess]
  )

  const handleDropVariant = useCallback(
    async (variant: ResponseVariantExtended) => {
      if (!content || !selectedElement) {
        return
      }

      const variantId = getVariantId(variant)
      if (!variantId) {
        return
      }
      setDroppingVariantIds((prev) => [...prev, variantId])

      try {
        await dropFn(variantId)
        setSelectedVariantIds((prev) => prev.filter((id) => id !== variantId))
        onDropSuccess?.(variantId)
      } catch (error) {
        errorToast('Failed to drop variant. Please try again.')
      } finally {
        setDroppingVariantIds((prev) => prev.filter((id) => id !== variantId))
      }
    },
    [content, selectedElement, dropFn, onDropSuccess]
  )

  const handleBulkApprove = useCallback(() => {
    if (selectedVariantIds.length === 0 || !content || !selectedElement) {
      return
    }

    const originalSelectedIds = [...selectedVariantIds]
    setApprovingVariantIds(originalSelectedIds)

    approveSingleVariant.mutate(
      {
        accountId: content.account_id,
        content_id: content._id,
        element_id: Number(selectedElement.element_id),
        variant_ids: originalSelectedIds,
      },
      {
        onSuccess: () => {
          setSelectedVariantIds([])
          onBulkApproveSuccess?.(originalSelectedIds)
        },
        onError: () => {
          errorToast('Failed to approve variants. Please try again.')
        },
        onSettled: () => {
          setApprovingVariantIds([])
        },
      }
    )
  }, [
    selectedVariantIds,
    content,
    selectedElement,
    approveSingleVariant,
    onBulkApproveSuccess,
  ])

  const handleBulkDrop = useCallback(async () => {
    if (selectedVariantIds.length === 0) {
      return
    }

    const originalSelectedIds = [...selectedVariantIds]
    setDroppingVariantIds(originalSelectedIds)

    try {
      await Promise.all(
        originalSelectedIds.map((variantId) => dropFn(variantId))
      )

      setSelectedVariantIds([])
      onBulkDropSuccess?.(originalSelectedIds)
    } catch (error) {
      errorToast('Failed to drop variants. Please try again.')
    } finally {
      setDroppingVariantIds([])
    }
  }, [selectedVariantIds, dropFn, onBulkDropSuccess])

  return {
    selectedVariantIds,
    approvingVariantIds,
    droppingVariantIds,
    handleRowClick,
    handleCheckboxClick,
    handleSelectAll,
    handleApproveVariant,
    handleDropVariant,
    handleBulkApprove,
    handleBulkDrop,
  }
}
